import React, { useEffect, useRef, useState } from 'react'
import { convertStorage, parseFileInfoFromUrl } from '../../utils/IncidentUtil'

import { Close } from '@carbon/icons-react'
import { DDSTypography } from 'den-design-system'
import FilePreviewCard from './FileTypes'
import { FileUploader } from 'react-drag-drop-files'
import SvgPreview from '../../components/common/TextAreaDrafts/PreviewSVG'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import Upload from '../../assets/icons/upload.png'
import pallete from '../../global/newPallete'
import { pdfjs } from 'react-pdf'

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

interface MultiMediaProps {
  type: 'create' | 'update'
  mediaFiles: MediaFile[]
  setMediaFiles: any
  disabled: boolean
  updatedSize?: any
  attachedFilesLength?: any
  onFileUploadSuccess?: (fileUrl: string) => void
  incidentDetails?: any
  setIncidentDetails?: any
  setFilesToRemove?: any
}

interface MediaFile {
  id: string
  file: File
  fileUrl: string
}

const MultiMedia: React.FC<MultiMediaProps> = ({
  type = 'create',
  mediaFiles,
  setMediaFiles,
  disabled,
  updatedSize,
  attachedFilesLength,
  onFileUploadSuccess,
  incidentDetails,
  setIncidentDetails,
  setFilesToRemove
}) => {
  const [previewFile, setPreviewFile] = useState<MediaFile | null>(null)

  const handleFileRemove = (id: string) => {
    setMediaFiles((prev: MediaFile[]) => prev.filter((file) => file.id !== id))
  }

  const getTotalSizeUpdate = (): string => {
    let totalSize = 0

    mediaFiles.forEach((media) => {
      totalSize += media.file.size
    })

    const UpdatedSizeValue = totalSize + updatedSize

    return convertStorage(UpdatedSizeValue)
  }

  const getTotalSize = (): string => {
    let totalSize = 0

    mediaFiles.forEach((media) => {
      totalSize += media.file.size
    })

    return convertStorage(totalSize)
  }

  const handleFileRemoveUpdate = (indexToRemove: number) => {
    if (typeof indexToRemove !== 'number') return

    const updatedFiles = [
      ...incidentDetails.attachedFiles.slice(0, indexToRemove),
      ...incidentDetails.attachedFiles.slice(indexToRemove + 1)
    ]
    const fileBeingRemoved = incidentDetails.attachedFiles[indexToRemove]
    setFilesToRemove((prev: string[]) => [...prev, fileBeingRemoved])
    setIncidentDetails((prev: any) => ({
      ...prev,
      attachedFiles: updatedFiles
    }))
  }

  const handleFileUpload = async (files: File[] | FileList) => {
    try {
      if (!files || files?.length === 0) {
        throw new Error('No files provided for upload.')
      }

      if (type === 'update') {
        let totalSize = mediaFiles.reduce(
          (sum, media) => sum + media.file.size,
          0
        )

        totalSize += updatedSize

        const newFilesSize = Array.from(files).reduce(
          (sum, file) => sum + file.size,
          0
        )

        const combinedSizeBytes = totalSize + newFilesSize
        const combinedSizeMB = combinedSizeBytes / (1024 * 1024)

        if (combinedSizeMB > 10) {
          ToastNotification({
            type: 'error',
            message:
              'Total file size exceeds the 10MB limit. Please remove some files or upload smaller ones.'
          })
          return
        }
      } else {
        const currentTotalSizeMB = mediaFiles.reduce(
          (sum, media) => sum + media.file.size / (1024 * 1024),
          0
        )

        const newFilesSizeMB = Array.from(files).reduce(
          (sum, file) => sum + file.size / (1024 * 1024),
          0
        )

        const combinedSizeMB = currentTotalSizeMB + newFilesSizeMB
        if (combinedSizeMB > 10) {
          ToastNotification({
            type: 'error',
            message:
              'Total file size exceeds the 10MB limit. Please remove some files or upload smaller ones.'
          })
          return
        }
      }

      const uploadedFiles: MediaFile[] = Array.from(files).map((file) => ({
        id: `${file.name}-${crypto.randomUUID()}`,
        file,
        fileUrl: URL.createObjectURL(file)
      }))

      setMediaFiles((prev: any) => [...prev, ...uploadedFiles])

      uploadedFiles.forEach((file) => {
        onFileUploadSuccess?.(file.fileUrl)
      })
    } catch (error) {
      console.error('File upload failed:', error)
      ToastNotification({ type: 'error', message: 'File upload failed!' })
    }
  }

  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    if (previewFile?.file.type.includes('video')) {
      const videoElement = videoRef.current
      if (videoElement) {
        videoElement
          .play()
          .catch((error: any) => console.error('Autoplay prevented:', error))
      }
    }
  }, [previewFile])

  return (
    <div className='flex flex-col gap-[4px]'>
      <div className=''>
        <DDSTypography.Paragraph
          size='para'
          variant='medium'
          className='mb-[12px] mt-[8px]'
          color={pallete.colors.textDark3}
        >
          Add Attachments
        </DDSTypography.Paragraph>
        <FileUploader
          handleChange={handleFileUpload}
          name='file'
          multiple
          disabled={disabled}
        >
          <div
            className='flex flex-col w-full h-full cursor-pointer items-center gap-[4px] py-[20px] px-[108px]'
            style={{
              borderRadius: '10px',
              border: '1px dashed #D9E0EA',
              background: 'rgba(237, 245, 255, 0.14)'
            }}
          >
            <img
              src={Upload}
              width='24px'
              height='24px'
              style={{
                filter:
                  'invert(32%) sepia(100%) saturate(4565%) hue-rotate(208deg) brightness(97%) contrast(95%)'
              }}
            />

            <div>
              <DDSTypography.Paragraph
                size='caption'
                variant='semiBold'
                color={pallete.colors.textDark6}
                className='text-center '
              >
                Upload your file here
              </DDSTypography.Paragraph>
            </div>
          </div>
        </FileUploader>
      </div>

      <div className='mt-[5px] flex flex-col gap-[5px]'>
        {mediaFiles.length > 0 && (
          <div>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              color={pallete.colors.textDark4}
            >
              Attachments & Reference links
            </DDSTypography.Paragraph>
          </div>
        )}

        <div className='flex flex-wrap  rounded-lg w-full'>
          <>
            {mediaFiles.map((media) => {
              const isVideo = media.file?.type.includes('video')

              const isHeic =
                media.file?.type.includes('heic') ||
                media.file?.type.includes('heif') ||
                media.file?.type.endsWith('.heic') ||
                media.file?.type.endsWith('.heif')
              const isImage = media.file?.type.includes('image/') && !isHeic

              return (
                <div key={media.id} className='group mr-[8px]'>
                  {isVideo && (
                    <div className='w-[192px] h-[128px] flex justify-center relative'>
                      <video
                        src={media.fileUrl}
                        className='object-cover rounded-md w-full h-full'
                        controls
                        muted
                        playsInline
                      />
                      <button
                        style={{
                          backgroundColor: pallete.colors.lightWhite,
                          borderColor: pallete.colors.borderWhite
                        }}
                        className='absolute -top-[3px] -right-[3px] w-[24px] h-[24px] rounded-full  text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-200 border-[2px]'
                        onClick={() => handleFileRemove(media.id)}
                      >
                        <Close size={12} />
                      </button>
                    </div>
                  )}

                  {isImage && (
                    <div className='w-[192px] h-[128px] flex justify-center relative'>
                      <img
                        src={media.fileUrl}
                        alt='media'
                        className='object-cover rounded-md w-full h-full'
                      />

                      <button
                        style={{
                          backgroundColor: pallete.colors.lightWhite,
                          borderColor: pallete.colors.borderWhite
                        }}
                        className='absolute -top-[3px] -right-[3px] w-[24px] h-[24px] rounded-full  text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-200 border-[2px]'
                        onClick={() => handleFileRemove(media.id)}
                      >
                        <Close size={12} />
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </>

          {incidentDetails?.attachedFiles?.map((media: any, index: number) => {
            const isHeic =
              media.includes('heic') ||
              media.includes('heif') ||
              media.endsWith('.heic') ||
              media.endsWith('.heif')
            const isImage = media.includes('image/') && !isHeic
            const isVideo = media.includes('video')
            const isSVG = media.endsWith('.svg')

            return (
              <div key={media?.id} className='group mr-[8px]'>
                {isVideo && (
                  <div className='w-[192px] h-[128px] flex justify-center relative'>
                    <video
                      src={media}
                      className='object-cover rounded-md w-full h-full'
                      controls
                      muted
                      playsInline
                    />
                    <button
                      style={{
                        backgroundColor: pallete.colors.lightWhite,
                        borderColor: pallete.colors.borderWhite
                      }}
                      className='absolute -top-[3px] -right-[3px] w-[24px] h-[24px] rounded-full  text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-200 border-[2px]'
                      onClick={() => handleFileRemoveUpdate(index)}
                    >
                      <Close size={12} />
                    </button>
                  </div>
                )}

                {isImage && (
                  <div className='w-[192px] h-[128px] flex justify-center relative'>
                    {isSVG ? (
                      <SvgPreview url={media} />
                    ) : (
                      <img
                        src={media}
                        alt='media'
                        className='object-cover rounded-md w-full h-full'
                      />
                    )}

                    <button
                      style={{
                        backgroundColor: pallete.colors.lightWhite,
                        borderColor: pallete.colors.borderWhite
                      }}
                      className='absolute -top-[3px] -right-[3px] w-[24px] h-[24px] rounded-full  text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-200 border-[2px]'
                      onClick={() => handleFileRemoveUpdate(index)}
                    >
                      <Close size={12} />
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className='flex flex-wrap pt-[12px] gap-[12px]'>
          {mediaFiles.map((media) => {
            const fileType = media.file?.type || ''
            const fileName = media.file?.name?.toLowerCase() || ''

            const isHeic =
              fileType.includes('heic') ||
              fileType.includes('heif') ||
              fileName.endsWith('.heic') ||
              fileName.endsWith('.heif')
            const isImage = fileType.includes('image/') && !isHeic
            const isVideo = fileType.startsWith('video')

            return (
              <React.Fragment key={media.id}>
                {!isImage && !isVideo && (
                  <FilePreviewCard
                    file={{
                      name: media.file?.name || 'file',
                      type: fileType,
                      url: media.fileUrl,
                      size: convertStorage(media.file?.size || 0)
                    }}
                    uploadType='create'
                    onClose={() => handleFileRemove(media.id)}
                  />
                )}
              </React.Fragment>
            )
          })}

          {incidentDetails?.attachedFiles?.map((media: any, index: number) => {
            const isHeic =
              media.includes('heic') ||
              media.includes('heif') ||
              media.endsWith('.heic') ||
              media.endsWith('.heif')
            const isImage = media.includes('image/') && !isHeic
            const isVideo = media.includes('video')
            const fileInfo = parseFileInfoFromUrl(media)
            const none = media.includes('blob')

            return (
              <React.Fragment key={media.fileUrl || media}>
                {!isImage && !isVideo && !none && (
                  <FilePreviewCard
                    file={{
                      name: fileInfo.name,
                      type: fileInfo.type,
                      url: media,
                      size: convertStorage(fileInfo.size)
                    }}
                    uploadType='create'
                    onClose={() => handleFileRemoveUpdate(index)}
                  />
                )}
              </React.Fragment>
            )
          })}
        </div>

        {type === 'create' && mediaFiles?.length > 0 && (
          <DDSTypography.Paragraph
            size='caption'
            variant='medium'
            color='textDark5'
          >
            {`${mediaFiles?.length} attachments added (${getTotalSize()})`}
          </DDSTypography.Paragraph>
        )}

        {type === 'update' && (
          <DDSTypography.Paragraph
            size='caption'
            variant='medium'
            color='textDark5'
          >
            {`${attachedFilesLength} attachments added (${getTotalSizeUpdate()})`}
          </DDSTypography.Paragraph>
        )}
      </div>
    </div>
  )
}

export default MultiMedia
